import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, ShieldAlert, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccountStatus, type AccountAlert } from '@/hooks/queries/useAccountStatus';
import { derivePhase } from '@/lib/billing/phase';

const SEVERITY_CLASSES: Record<string, string> = {
  info: 'border-warning-dark/30 bg-warning-light text-warning-dark',
  warning:
    'border-orange-500/30 bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200',
  critical: 'border-error-dark/30 bg-error-light text-error-dark',
};

function CountdownDisplay({ date }: { date: string }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    function update() {
      const diff = new Date(date).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft('0j');
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      setTimeLeft(days > 0 ? `${days}j ${hours}h` : `${hours}h`);
    }
    update();
    const interval = setInterval(update, 60_000);
    return () => clearInterval(interval);
  }, [date]);

  if (!timeLeft) return null;

  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-white/60 px-2 py-0.5 text-xs font-bold dark:bg-black/20">
      <Clock className="h-3 w-3" />
      {timeLeft}
    </span>
  );
}

function AlertBanner({ alert, onDismiss }: { alert: AccountAlert; onDismiss?: () => void }) {
  const tone = SEVERITY_CLASSES[alert.severity] ?? SEVERITY_CLASSES.info;
  const Icon = alert.level >= 3 ? ShieldAlert : AlertTriangle;
  const cta = alert.cta_url ?? '/subscription';

  return (
    <div className={`mx-4 mt-3 rounded-lg border px-4 py-3 ${tone}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Icon className="h-5 w-5 shrink-0" />
        <div className="min-w-[220px] flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">{alert.title}</p>
            {alert.countdown_date && <CountdownDisplay date={alert.countdown_date} />}
          </div>
          <p className="text-xs opacity-90">{alert.message}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline" className="bg-white/80 dark:bg-card/80">
            <Link to={cta}>Voir les details</Link>
          </Button>
          {alert.dismissable && onDismiss && (
            <button
              onClick={onDismiss}
              className="rounded-md p-1 opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AccountStatusBanner() {
  const { data } = useAccountStatus();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('dismissed_alerts');
      if (stored) {
        setDismissed(new Set(JSON.parse(stored) as string[]));
      }
    } catch {
      // ignore
    }
  }, []);

  const handleDismiss = useCallback((code: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(code);
      sessionStorage.setItem('dismissed_alerts', JSON.stringify([...next]));
      return next;
    });
  }, []);

  if (!data) return null;
  if (data.access_state === 'active' && data.alerts.length === 0) return null;
  if (data.access_state === 'trial_active' && data.alerts.length === 0) return null;

  // Defer to ExpiryCountdownBanner for warning/grace and to ReconversionScreen for blocked.
  const { phase } = derivePhase(
    data.access_state,
    data.days_to_expiry,
    data.grace_days_left,
    data.trial_days_left,
    data.write_restricted,
  );
  if (phase === 'warning' || phase === 'grace' || phase === 'reconversion') return null;

  // Don't render blocking alerts in banner (handled by ReconversionScreen)
  const visibleAlerts = data.alerts.filter(
    (a) => a.severity !== 'blocking' && !dismissed.has(a.code),
  );

  const alert = visibleAlerts[0];
  if (!alert) return null;

  return (
    <AlertBanner
      alert={alert}
      onDismiss={alert.dismissable ? () => handleDismiss(alert.code) : undefined}
    />
  );
}
